import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserMinus, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getAccountsTeam, setUserAccountsAccess } from "@/lib/api";
import { Empty, TH, TR, errMsg } from "@/components/accounts/shared";

// Who can open Accounts. The head of accounts (and admins) add and remove team members;
// admins make someone head of accounts in Global Admin.
export default function AccountsTeam() {
  const [team, setTeam] = useState(null);
  const [pick, setPick] = useState("");
  const load = useCallback(() => getAccountsTeam().then(setTeam).catch((e) => toast.error(errMsg(e, "Could not load the team"))), []);
  useEffect(() => { load(); }, [load]);
  const set = async (u, on) => {
    try {
      await setUserAccountsAccess(u.user_id, on);
      toast.success(`${u.name || u.email} ${on ? "added to" : "removed from"} the accounts team`);
      setPick("");
      load();
    } catch (e) { toast.error(errMsg(e, "Could not update")); }
  };
  if (!team) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const role = (u) => (u.admin ? "Admin" : u.accounts_head ? "Head of accounts" : "Accounts team");
  return (
    <div className="space-y-4 max-w-3xl" data-testid="accounts-team">
      {team.can_manage && (
        <div className="card-surface px-4 py-3 flex flex-wrap items-center gap-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1 min-w-[220px] bg-[#0a0a0a] border border-[#262626] rounded-md h-9 px-2 text-sm" data-testid="team-pick">
            <option value="">Add someone to the accounts team…</option>
            {team.others.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {u.email}</option>)}
          </select>
          <Button size="sm" disabled={!pick} onClick={() => set(team.others.find((u) => u.user_id === pick), true)} className="bg-blue-600 hover:bg-blue-500" data-testid="team-add">
            <UserPlus className="h-3.5 w-3.5 mr-1" /> Add</Button>
        </div>
      )}
      <div className="card-surface overflow-x-auto">
        {team.members.length === 0 ? <Empty>No one yet.</Empty> : (
          <table className="w-full text-sm">
            <thead><tr className={TH}><th className="px-3 py-2 font-medium">Person</th><th className="px-3 py-2 font-medium">Access</th><th /></tr></thead>
            <tbody>
              {team.members.map((u) => (
                <tr key={u.user_id} className={TR}>
                  <td className="px-3 py-2"><div className="text-zinc-200">{u.name}</div><div className="text-[11px] text-zinc-500">{u.email}</div></td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{role(u)}</td>
                  <td className="px-3 py-2 text-right">
                    {team.can_manage && u.accounts_access && !u.admin && !u.accounts_head && (
                      <button onClick={() => set(u, false)} className="text-zinc-500 hover:text-rose-300 text-xs inline-flex items-center gap-1"><UserMinus className="h-3.5 w-3.5" /> Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-[11px] text-zinc-500">Admins and above always have Accounts. Head of accounts is set by an admin in Global Admin.</p>
    </div>
  );
}
